import {
  accentColorType,
  accentPositionType,
  backgroundColorType,
  borderColorType,
  borderRadiusType,
  cardPaddingType,
  elevationType,
  listStyleType
} from '@toasttab/shared/src/components/molecules/CardTypes/types.d'
import { badgeColorType } from '@toasttab/shared/src/components/atoms/Badge/Badge.d'

export const accentColor = (accent: accentColorType) => {
  switch (accent) {
    case 'brand-50':
      return 'bg-brand-50'
    case 'blue-75':
      return 'bg-blue-75'
    case 'yellow-50':
      return 'bg-yellow-50'
    case 'null':
    default:
      return ''
  }
}

export const accentPosition = (accentPos: accentPositionType) => {
  // This seems unnecessary, but PurgeCSS needs to see the actual class names in the code or it strips them out of the stylesheet
  switch (accentPos) {
    case 'top':
      return 'top'
    case 'bottom':
      return 'bottom'
    case 'null':
    default:
      return ''
  }
}

export const backgroundColor = (bgColor: backgroundColorType) => {
  // See comment in accentPosition, same thing here. We need these for PurgeCSS
  switch (bgColor) {
    case 'bg-gray-0':
      return 'bg-gray-0'
    case 'bg-blue-50':
      return 'bg-blue-50'
    case 'bg-brand-50':
      return 'bg-brand-50'
    case 'bg-black-12':
      return 'bg-brand-50'
    case 'transparent':
      return ''
    case 'bg-white':
    default:
      return 'bg-white'
  }
}

export const badgeColor = (badge: badgeColorType) => {
  switch (badge) {
    case 'brand-50':
      return 'bg-brand-50'
    case 'blue-75':
      return 'bg-blue-75'
    case 'yellow-50':
      return 'bg-yellow-50'
    case 'bg-lavender-0':
      return 'bg-lavender-0'
    case 'null':
    default:
      return ''
  }
}

export const borderColor = (border: borderColorType) => {
  switch (border) {
    case 'light':
      return `border-card-light`
    case 'dark':
      return `border-card-dark`
    case 'null':
    default:
      return ''
  }
}

export const borderRadius = (radius: borderRadiusType) => {
  switch (radius) {
    case 'Small':
      return 'rounded-sm'
    case 'Medium':
      return 'rounded-md'
    case 'Large':
      return 'rounded-lg'
    case 'XL':
      return 'rounded-xl'
    case 'Round':
      return 'rounded-round'
    default:
      return 'rounded-none'
  }
}

const paddingArray: any = {
  null: '0',
  none: '0',
  '16': '4',
  '24': '6',
  '32': '8',
  '40': '10',
  '48': '12'
}

export const cardPadding = (padding: cardPaddingType) => {
  if (!padding) {
    return ''
  }
  const nullValues = ['', 'null']
  if (typeof padding === 'string') {
    return `p-${paddingArray[padding]}`
  } else {
    const topPadding = !nullValues.includes(padding.paddingTop)
      ? `md:pt-${paddingArray[padding.paddingTop]}`
      : 'undefined'
    const mobileTopPadding = !nullValues.includes(padding.mobileTop)
      ? `pt-${paddingArray[padding.mobileTop]}`
      : `pt-${paddingArray[padding.paddingTop]}`

    const bottomPadding = !nullValues.includes(padding.paddingBottom)
      ? `md:pb-${paddingArray[padding.paddingBottom]}`
      : `md:pb-${paddingArray[padding.paddingTop]}`
    const mobileBottomValue = !nullValues.includes(padding.paddingBottom)
      ? paddingArray[padding.paddingBottom]
      : paddingArray[padding.paddingTop]
    const mobileBottomPadding = !nullValues.includes(padding.mobileBottom)
      ? `pb-${paddingArray[padding.mobileBottom]}`
      : `pb-${mobileBottomValue}`

    const leftPadding = !nullValues.includes(padding.paddingLeft)
      ? `md:pl-${paddingArray[padding.paddingLeft]}`
      : 'undefined'
    const mobileLeftPadding = !nullValues.includes(padding.mobileLeft)
      ? `pl-${paddingArray[padding.mobileLeft]}`
      : `pl-${paddingArray[padding.paddingLeft]}`

    const rightPadding = !nullValues.includes(padding.paddingRight)
      ? `md:pr-${paddingArray[padding.paddingRight]}`
      : `md:pr-${paddingArray[padding.paddingLeft]}`
    const mobileRightValue = !nullValues.includes(padding.paddingRight)
      ? paddingArray[padding.paddingRight]
      : paddingArray[padding.paddingLeft]
    const mobileRightPadding = !nullValues.includes(padding.mobileRight)
      ? `pr-${paddingArray[padding.mobileRight]}`
      : `pr-${mobileRightValue}`

    // We've been getting some `undefined` values in the padding classes, so this will filter them out
    const paddingValues = [
      topPadding,
      bottomPadding,
      leftPadding,
      rightPadding,
      mobileTopPadding,
      mobileBottomPadding,
      mobileLeftPadding,
      mobileRightPadding
    ]
    const paddingArr = paddingValues.filter(
      (padding) => !padding.includes('undefined')
    )
    return paddingArr.join(' ')
  }
}

export const elevation = (elevationStyle: elevationType) => {
  switch (elevationStyle) {
    case 'raised':
    case 'Raised':
      return 'shadow-raised'
    case 'elevated':
    case 'Elevated':
      return 'shadow-elevated'
    case 'floating':
    case 'Floating':
      return 'shadow-floating'
    case 'flat':
    case 'Flat':
      return 'shadow-none'
    default:
      return ''
  }
}

export const listStyle = (list: listStyleType) => {
  switch (list) {
    case 'list-bullet-gray':
      return 'before:content-icon-gray-bullet'
    case 'list-checkmark-gray':
      return 'before:content-icon-gray-check'
    case 'list-checkmark-circle-solid-gray':
      return 'before:content-icon-gray-checkmark-circle'
    case 'list-checkmark-orange':
      return 'before:content-icon-orange-check'
    case 'list-checkmark-circle-solid-orange':
      return 'before:content-icon-orange-circle-solid-checkmark'
    case 'list-plus-circle-gray':
      return 'before:content-icon-gray-plus-circle'
    case 'list-checkmark-green':
      return 'before:content-icon-checkmark-green'
    default:
      return ''
  }
}
